import './TQServiceRequestResponseDetails.scss';

import React, { memo, useMemo, useState } from 'react';

import { Button, Form, Input, message, Progress, Typography } from 'antd';
import { CgArrowTopRight } from 'react-icons/cg';
import { PiPaperclip } from 'react-icons/pi';
import TMIcon from 'tm-icons-library';

import AttachedFileItem from '../../../../components/AttachedFileItem/AttachedFileItem';
import CustomAvatar from '../../../../components/CustomAvatar/CustomAvatar';
import CustomReadMoreContent from '../../../../components/CustomReadMoreText/CustomReadMoreContent';
import ProfileItem from '../../../../components/ProfileItem/ProfileItem';
import DotsFlashingLoader from '../../../../components/V2/Loader/DotsFlashingLoader';
import { useTheme } from '../../../../context/ThemeProvider';
import { uploadFilesInServiceRequest } from '../../../../context/thunks/tqConnectServiceRequestThunk';
import { useCreateTQServiceRequestResponse } from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import { DEFAULT_AVATAR, DELETED_USER } from '../../../../shared/data';
import showAppError from '../../../../shared/error';
import { downloadFileWithProgress } from '../../../../shared/fileUtils';
import { useAppDispatch, useAppNavigate } from '../../../../shared/hooks';
import {
  checkIfCurrentLoggedInUser,
  getReadableDateAndTimeFormat,
  getSingularOrPlural,
} from '../../../../shared/utils';
import { IFileAttachment } from '../../../../types/fileTypes';
import { ROUTES } from '../../../../types/routes';
import {
  ITQCreateServiceRequestResponseBody,
  ITQServiceRequest,
  ITQServiceRequestResponse,
  ITQServiceRequestResponseDetailsUser,
} from '../../../../types/tqConnectTypes/service-requests';
import { ACCEPT_FILE_TYPES, TQ_VALIDATION } from '../../constants';
import TQConnectBadge from '../TQConnectBadge';
import TQConnectIcon from '../TQConnectIcon';
import AddAttachments from './AddAttachments';
import FormItemLabel from './FormItemLabel';

interface Props {
  mode: 'create' | 'view' | 'confirm';
  canAcceptOrReject: boolean;
  serviceRequestId: string;
  respondedBy?: ITQServiceRequestResponseDetailsUser | null;
  response?: ITQServiceRequestResponse;
  formValues: ITQCreateServiceRequestResponseBody;
  onChangeFormValues: <Key extends keyof ITQCreateServiceRequestResponseBody>(
    key: Key,
    value: ITQCreateServiceRequestResponseBody[Key],
  ) => void;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  onSuccess: () => void;
  onAccept: () => void;
  onReject: () => void;
  isRejecting: boolean;
  reset: () => void;
  serviceRequestDetails?: ITQServiceRequest;
  onCloseDetails?: () => void;
}

const TQServiceRequestResponseDetails: React.FC<Props> = ({
  mode,
  canAcceptOrReject,
  serviceRequestId,
  respondedBy,
  response,
  formValues,
  onChangeFormValues,
  attachments,
  setAttachments,
  onSuccess,
  onAccept,
  onReject,
  isRejecting,
  reset,
  onCloseDetails,
}) => {
  const navigate = useAppNavigate();
  const { colors: COLORS } = useTheme();

  const dispatch = useAppDispatch();

  const {
    createServiceRequestResponse,
    isPending: isSendingServiceRequestResponse,
  } = useCreateTQServiceRequestResponse(serviceRequestId);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const isSaving = useMemo(() => {
    return isSendingServiceRequestResponse || isUploading;
  }, [isSendingServiceRequestResponse, isUploading]);

  // show actions only if the current user is the responder or the current user can accept or reject the offer if not already accepted or rejected
  const hideActions = useMemo(() => {
    return (
      (mode === 'view' &&
        (checkIfCurrentLoggedInUser(respondedBy?._id || '') ||
          !canAcceptOrReject)) ||
      response?.status === 'accepted' ||
      response?.status === 'rejected'
    );
  }, [mode, respondedBy?._id, canAcceptOrReject, response?.status]);

  const closeModal = () => {
    reset();
  };

  const submitResponse = async () => {
    if (!formValues.description.trim()) {
      message.warning('Please add how you can help');
      return;
    }

    try {
      let uploadedFiles: IFileAttachment[] = [];

      if (attachments.length > 0) {
        const resp = await dispatch(
          uploadFilesInServiceRequest(attachments, setIsUploading, (event) => {
            if (typeof event.progress === 'number') {
              setUploadProgress(event.progress);
            }
          }),
        );

        if (resp && resp?.length > 0) {
          uploadedFiles = resp;
        }
      }

      const reqBody: ITQCreateServiceRequestResponseBody = {
        description: formValues.description,
        attachments: [...(formValues.attachments || []), ...uploadedFiles],
      };

      createServiceRequestResponse(
        {
          serviceRequestId,
          data: reqBody,
        },
        {
          onSuccess: () => {
            message.success(`Sent your offer`);
            onSuccess();
          },
          onError: () => {
            setUploadProgress(0);
            setIsUploading(false);
          },
        },
      );
    } catch (error) {
      showAppError(error);
    } finally {
      setIsUploading(false);
    }
  };

  // View mode: layout matching Response details design (bottom sheet)
  if (mode === 'view') {
    const attachmentCount = response?.attachments?.length ?? 0;
    return (
      <div className="tq-response-details">
        {onCloseDetails ? (
          <header className="tq-response-details__header">
            <h2 className="tq-response-details__title">Response details</h2>
            <button
              type="button"
              className="tq-response-details__close"
              onClick={onCloseDetails}
              aria-label="Close">
              <TMIcon name="close" size={20} color={COLORS.ICON} />
            </button>
          </header>
        ) : null}

        <div className="tq-response-details__user">
          <CustomAvatar
            size={48}
            src={respondedBy?.profilePicUrl || DEFAULT_AVATAR}
            name={respondedBy?.name}
            border={0}
            className="tq-response-details__avatar"
          />
          <div className="tq-response-details__user-meta">
            <span className="tq-response-details__user-name">
              {respondedBy?.name || DELETED_USER}
            </span>
            {(typeof respondedBy?.tqConnectScore === 'number' ||
              respondedBy?.tqReviewReceived != null) && (
              <span className="tq-response-details__user-stats">
                {typeof respondedBy?.tqConnectScore === 'number' && (
                  <span className="tq-response-details__user-score">
                    <span className="tq-response-details__score-icon">
                      <TQConnectIcon size={20} />
                    </span>
                    {respondedBy.tqConnectScore} TQ
                  </span>
                )}
                {typeof respondedBy?.tqConnectScore === 'number' &&
                  respondedBy?.tqReviewReceived != null && (
                    <span className="tq-response-details__user-dot" />
                  )}
                {respondedBy?.tqReviewReceived != null && (
                  <span>
                    {respondedBy.tqReviewReceived}{' '}
                    {getSingularOrPlural(
                      respondedBy.tqReviewReceived,
                      'request',
                    )}{' '}
                    completed
                  </span>
                )}
              </span>
            )}
            <span className="tq-response-details__user-time">
              {response?.createdAt
                ? getReadableDateAndTimeFormat(response.createdAt, {
                    relativeTime: true,
                  })
                : null}
            </span>
          </div>
        </div>
        <Button
          htmlType="button"
          className="siteBtn grayBtn outlineBtn tq-response-details__view-profile"
          onClick={() => {
            if (respondedBy?._id) {
              onCloseDetails?.();
              navigate(ROUTES.USER_PROFILE, {
                userId: respondedBy._id,
              });
            }
          }}>
          View Profile <CgArrowTopRight size={18} color={COLORS.ICON} />
        </Button>

        <CustomReadMoreContent
          lines={5}
          expandable
          className="tq-response-details__description">
          {response?.description}
        </CustomReadMoreContent>

        {attachmentCount > 0 ? (
          <div className="tq-response-details__attachments">
            <h3 className="tq-response-details__attachments-title">
              <PiPaperclip size={16} color={COLORS.ICON} />
              Attachments ({attachmentCount})
            </h3>
            <div className="tq-response-details__attachment-list">
              {response?.attachments?.map((attachment) => (
                <AttachedFileItem
                  key={attachment.url}
                  fileName={attachment.name}
                  fileSize={attachment.size}
                  fileExtension={attachment.type}
                  onDownload={() =>
                    downloadFileWithProgress(attachment.url, attachment.name)
                  }
                />
              ))}
            </div>
          </div>
        ) : null}

        {response?.status === 'rejected' ? (
          <div className="tq-response-details__rejected-info">
            <TMIcon
              name="close-circle-outline"
              size={20}
              color={COLORS.ERROR}
            />
            This offer was rejected on{' '}
            {getReadableDateAndTimeFormat(response?.updatedAt, {
              relativeTime: false,
            })}
          </div>
        ) : null}

        {!hideActions ? (
          <div
            className="tq-response-details__footer"
            style={{ flexDirection: 'column' }}>
            <Button
              className="siteBtn accentBtnV1 successBtn"
              disabled={isRejecting}
              onClick={onAccept}
              block>
              Accept
            </Button>
            <Button
              type="link"
              danger
              loading={isRejecting}
              disabled={isRejecting}
              onClick={onReject}
              style={{ fontSize: 14, fontWeight: 600, color: COLORS.ERROR }}>
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div className="modal__content create-or-view-offer">
        {uploadProgress > 0 ? (
          <div className="modal__content__uploading">
            <div className="modal__content__uploading__inner">
              <div className="modal__content__uploading__title">
                <Typography.Title
                  level={4}
                  className="modal__content__uploading__title__text">
                  Finishing up
                </Typography.Title>
                <DotsFlashingLoader color={COLORS.WHITE} size={6} />
              </div>
              <Progress
                percent={Math.round(uploadProgress * 100)}
                status="active"
                strokeColor={COLORS.WHITE}
                showInfo={false}
                size="small"
                trailColor={COLORS.ICON}
              />
              <Typography.Paragraph className="modal__content__uploading__subtitle">
                Your help offer is being processed, it will be ready soon.
                Please wait...
              </Typography.Paragraph>
            </div>
          </div>
        ) : null}

        <div
          className="modal__content__body slimScroll transparent"
          style={{
            paddingBottom: hideActions ? 20 : undefined,
          }}>
          <ProfileItem
            className="content__header"
            avatar={respondedBy?.profilePicUrl || DEFAULT_AVATAR}
            avatarSize={48}
            title={respondedBy?.name || DELETED_USER}
            subtitle={
              typeof respondedBy?.tqConnectScore === 'number' ? (
                <>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: COLORS.TEXT2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}>
                    <TQConnectIcon size={20} /> {respondedBy?.tqConnectScore}
                  </span>

                  <TQConnectBadge
                    score={respondedBy?.tqConnectScore}
                    iconSize="sm"
                  />
                  {respondedBy?.tqReviewReceived ? (
                    <>
                      <span>•</span>
                      <span>
                        {respondedBy?.tqReviewReceived}{' '}
                        {getSingularOrPlural(
                          respondedBy?.tqReviewReceived,
                          'request',
                        )}{' '}
                        completed
                      </span>
                    </>
                  ) : null}
                </>
              ) : null
            }
            action={null}
            titleStyle={{ marginBottom: 2, fontSize: 14 }}
            subtitleStyle={{
              width: '100%',
              fontSize: 12,
              color: COLORS.TEXT2,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            actionStyle={{
              alignSelf: 'start',
            }}
            style={{ flexShrink: 0, marginBottom: 24 }}
          />

          <Form layout="vertical" style={{ paddingInline: 4 }}>
            {mode === 'create' ? (
              <Form.Item
                className="tmFormItem"
                label="How you can help?"
                rules={[
                  {
                    required: true,
                    message: 'Please add how you can help',
                  },
                  // restrict only empty spaces
                  () => ({
                    validator(_, value) {
                      if (value && value.trim() === '') {
                        return Promise.reject(
                          new Error(`Offer message can't be empty`),
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
                // required
              >
                <Input.TextArea
                  size="large"
                  placeholder="Draft your offer message here..."
                  value={formValues.description}
                  onChange={(e) =>
                    onChangeFormValues('description', e.target.value)
                  }
                  maxLength={
                    TQ_VALIDATION.MAX_SERVICE_REQUEST_RESPONSE_DESCRIPTION_LENGTH
                  }
                  showCount
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  autoFocus
                  spellCheck
                />
              </Form.Item>
            ) : (
              <CustomReadMoreContent
                lines={5}
                className="content__description"
                style={{ marginBottom: 24 }}>
                {response?.description}
              </CustomReadMoreContent>
            )}

            {mode === 'create' ||
            (response?.attachments && response?.attachments?.length > 0) ? (
              <Form.Item
                className="tmFormItem"
                label={
                  <FormItemLabel
                    label={
                      mode === 'create'
                        ? `${getSingularOrPlural(TQ_VALIDATION.MAX_SERVICE_REQUEST_FILES_COUNT, 'Attachment')} (optional)`
                        : (response?.attachments && (
                            <>
                              <PiPaperclip
                                size={16}
                                color={COLORS.ICON}
                                style={{ marginRight: 4 }}
                              />
                              {getSingularOrPlural(
                                response?.attachments?.length,
                                'Attachment',
                              )}{' '}
                              ({response?.attachments?.length})
                            </>
                          )) ||
                          undefined
                    }
                    info={
                      mode === 'create'
                        ? `You can upload up to ${TQ_VALIDATION.MAX_SERVICE_REQUEST_FILES_COUNT} ${getSingularOrPlural(TQ_VALIDATION.MAX_SERVICE_REQUEST_FILES_COUNT, 'file')} (Max ${TQ_VALIDATION.MAX_SERVICE_REQUEST_FILE_SIZE_IN_MB} MB each) in ${[
                            'image',
                            'video',
                            'docx',
                            'pdf',
                          ]
                            .map((t) => {
                              const type = t as keyof typeof ACCEPT_FILE_TYPES;

                              return ACCEPT_FILE_TYPES[
                                type as keyof typeof ACCEPT_FILE_TYPES
                              ]
                                ? ACCEPT_FILE_TYPES[type].join(', ')
                                : type;
                            })
                            .join(', ')
                            .toUpperCase()} format.`
                        : undefined
                    }
                  />
                }
                style={{
                  flexFlow: 'column',
                  marginBottom: hideActions ? 0 : undefined,
                }}>
                <AddAttachments
                  mode={mode === 'create' ? 'add' : 'view'}
                  maxFileCount={TQ_VALIDATION.MAX_SERVICE_REQUEST_FILES_COUNT}
                  maxFileSize={TQ_VALIDATION.MAX_SERVICE_REQUEST_FILE_SIZE}
                  allowedFileTypes={['image', 'video', 'docx', 'pdf']}
                  attachmentList={response?.attachments}
                  attachments={attachments}
                  setAttachments={setAttachments}
                />
              </Form.Item>
            ) : null}
          </Form>
        </div>

        {response?.status === 'rejected' ? (
          <div className="tq-response-details__rejected-info">
            <TMIcon
              name="close-circle-outline"
              size={20}
              color={COLORS.ERROR}
            />
            This offer was rejected on{' '}
            {getReadableDateAndTimeFormat(response?.updatedAt, {
              relativeTime: false,
            })}
          </div>
        ) : null}

        {hideActions ? null : (
          <div
            style={{
              flexDirection: mode === 'create' ? 'row' : 'column',
              gap: 12,
            }}>
            {mode === 'create' ? (
              <>
                <Button
                  className="siteBtn grayBtn outlineBtn"
                  onClick={closeModal}
                  disabled={isSaving}
                  block>
                  Cancel
                </Button>

                <Button
                  className="siteBtn accentBtnV1"
                  onClick={submitResponse}
                  loading={isSaving}
                  disabled={isSaving}
                  block>
                  {isSaving ? 'Sending...' : 'Send help offer'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  className="siteBtn accentBtnV1 successBtn"
                  disabled={isRejecting}
                  onClick={onAccept}
                  block>
                  Accept
                </Button>

                <Button
                  type="link"
                  danger
                  loading={isRejecting}
                  disabled={isRejecting}
                  onClick={onReject}
                  style={{ fontSize: 14, fontWeight: 600 }}>
                  {isRejecting ? 'Rejecting...' : 'Reject'}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default memo(TQServiceRequestResponseDetails);
